/**
 * Topic-hub FAQ builder.
 *
 * Turns a topic box and its published articles into a real, answerable FAQ:
 * each question mirrors a search-style query about the topic and each answer
 * summarises the published guide that covers it, linking straight to it.
 */

export interface TopicFaqItem {
  q: string;
  a: string;
  href?: string;
  linkLabel?: string;
}

interface FaqArticle {
  title: string;
  slug: string;
  excerpt?: string;
}

interface FaqBox {
  title: string;
  slug: string;
  blurb?: string;
}

/** Stable hash so wording is identical on server and client. */
function hash(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const QUESTION_TEMPLATES = [
  (t: string) => `${stripYear(t)} — what do I need to know?`,
  (t: string) => `How does ${lower(stripYear(t))} work in 2026?`,
  (t: string) => `What does our guide to ${lower(stripYear(t))} cover?`,
  (t: string) => `Where can I read about ${lower(stripYear(t))}?`,
];

function stripYear(title: string): string {
  return title.replace(/\s*[:—-]?\s*\b20\d{2}\b\s*$/, "").trim() || title;
}

function lower(title: string): string {
  // Keep acronyms and proper nouns intact; only lowercase a leading plain word.
  return /^[A-Z][a-z]/.test(title) ? title.charAt(0).toLowerCase() + title.slice(1) : title;
}

function trimAnswer(text: string, max = 280): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
}

export function buildTopicFaq(box: FaqBox, articles: FaqArticle[]): TopicFaqItem[] {
  const items: TopicFaqItem[] = [];

  if (articles.length > 0) {
    const titles = articles.slice(0, 3).map((a) => stripYear(a.title));
    items.push({
      q: `What does the ${box.title} hub on LifeWestWay cover?`,
      a: trimAnswer(
        `${box.blurb || `Our ${box.title} hub collects the guides our editors publish on this topic.`} It currently holds ${articles.length} published guide${articles.length === 1 ? "" : "s"}, including ${titles.join(", ")}.`,
        320,
      ),
    });
  }

  for (const article of articles.slice(0, 10)) {
    const template = QUESTION_TEMPLATES[hash(article.slug) % QUESTION_TEMPLATES.length]!;
    items.push({
      q: template(article.title),
      a: trimAnswer(
        article.excerpt?.trim() ||
          `Our guide "${article.title}" walks through the rules, costs and steps involved, updated for 2026.`,
      ),
      href: `/topics/${box.slug}/${article.slug}`,
      linkLabel: article.title,
    });
  }

  if (articles.length > 0) {
    items.push({
      q: `How often are ${box.title} guides updated?`,
      a: trimAnswer(
        `Our editors review each ${box.title} guide whenever the underlying rules, rates or thresholds change, and republish it with a fresh update date so the figures you read stay current for 2026.`,
      ),
    });
  }

  return items;
}

/** FAQPage JSON-LD for a topic hub. Answers are plain text plus the guide URL. */
export function topicFaqJsonLd(
  items: TopicFaqItem[],
  baseUrl = "https://lifewestway.com",
): string | null {
  if (!items.length) return null;
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.href ? `${item.a} Read the full guide: ${baseUrl}${item.href}` : item.a,
      },
    })),
  });
}
